import { Button } from '../../../modules/design-system';
import type { ShippingAddress } from '../../../modules/api';
import { AddressLines } from './AddressLines';
import { DefaultBadge } from './DefaultBadge';

export interface AddressBlockProps {
  address: ShippingAddress;
  /** Render a "Change" link on the right (used on the New page). */
  onChange?: () => void;
}

/**
 * Read-only address summary. Shared by:
 *   • New — once the user picks an address, with a "Change" link.
 *   • Show — pure display, no Change link.
 *
 * White text on the dark Delivery card; a "Default" badge appears when this
 * is the player's default address.
 */
export function AddressBlock({ address, onChange }: AddressBlockProps) {
  // Themable under the `DeliveryYourAddress` namespace (the only consumer
  // of this component today — see YourAddressSection.tsx). `color` is
  // wired explicitly so AddressLines' `currentColor` cascade picks up the
  // operator's choice for the name + dimmed address lines.
  return (
    <div
      className="flex items-start justify-between gap-3 rounded-[20px] p-4"
      style={{
        border:
          '1px solid var(--co-DeliveryYourAddress-selectedBorder, rgba(171,174,179,0.20))',
        background:
          'linear-gradient(to bottom, ' +
          'var(--co-DeliveryYourAddress-selectedBgFrom, rgba(255,255,255,0.02)) 0%, ' +
          'var(--co-DeliveryYourAddress-selectedBgTo, rgba(255,255,255,0.02)) 100%)',
        color: 'var(--co-DeliveryYourAddress-selectedText, #FFFFFF)',
      }}
    >
      <div className="min-w-0">
        {address.isDefault && (
          <div className="mb-1.5">
            <DefaultBadge />
          </div>
        )}
        <AddressLines address={address} />
      </div>
      {onChange && (
        <Button variant="ghost" size="sm" onClick={onChange}>
          Change
        </Button>
      )}
    </div>
  );
}
